// Feed looping
// ------------
// The social feeds are finite. There are only so many people who match your
// filters, and a feed that stops when you reach the last one leaves you looking
// at an end-of-list message on an app whose entire job is to show you people.
//
// So the feeds wrap instead of ending. On reaching the last candidate the
// cursor resets to the top with a fresh ordering seed and starts another pass.
//
// The seed matters. Without a new one, lap two is lap one in the same order,
// which reads as a bug rather than as a second look. With one, the same people
// come back arranged differently, which is what "recycle" should feel like.
//
// Shared by Matches, Nearby and Explore so all three wrap identically.

/**
 * A feed item that knows which lap it belongs to.
 *
 * The same person appearing on lap 0 and lap 2 is two entries in the list and
 * needs two React keys, so `id` alone cannot be the key.
 */
export type Looped<T> = T & { loopKey: string };

export interface LoopCursor {
  /** Offset to request next, within the current pass. */
  offset: number;
  /** Completed passes through the pool. 0 is the first time through. */
  cycle: number;
  /** Ordering seed for this pass. */
  seed: string;
}

export const startLoop = (seed: string): LoopCursor => ({ offset: 0, cycle: 0, seed });

/** Stamps a freshly fetched page with the lap it arrived on. */
export const tagCycle = <T extends { id: string }>(page: T[], cycle: number): Looped<T>[] =>
  page.map((item) => ({ ...item, loopKey: `${item.id}#${cycle}` }));

/**
 * Appends a page, dropping anything already on screen.
 *
 * Deduping on loopKey rather than id is what makes wrapping work: the same
 * person twice in one pass is a paging artefact and gets dropped, while the
 * same person on the next pass is the whole point and gets through.
 */
export const appendLooped = <T extends { id: string }>(
  prev: Looped<T>[],
  page: Looped<T>[]
): Looped<T>[] => {
  const known = new Set(prev.map((item) => item.loopKey));
  return [...prev, ...page.filter((item) => !known.has(item.loopKey))];
};

/**
 * Where to read from next.
 *
 * A short page means the pool is spent, so the next request starts a new pass
 * from offset 0 rather than reading off the end.
 */
export const advanceCursor = (
  cursor: LoopCursor,
  received: number,
  pageSize: number,
  nextSeed: () => string
): LoopCursor =>
  received < pageSize
    ? { offset: 0, cycle: cursor.cycle + 1, seed: nextSeed() }
    : { ...cursor, offset: cursor.offset + received };

/**
 * Whether looping should stop.
 *
 * One case only: a request from the top of a pass that came back with nobody.
 * That means the pool is empty, and wrapping an empty pool spins — page 0
 * forever, rendering nothing. Anything else keeps looping.
 *
 * Deliberately answerable from the request alone, with no reference to what is
 * already on screen, so callers never have to reach into state to decide.
 */
export const poolIsEmpty = (cursor: LoopCursor, received: number): boolean =>
  cursor.offset === 0 && received === 0;
